"use client";

import React, { createContext, useContext, useReducer } from "react";
import { KB_CONFIG } from "@/src/helpers/knowledgeConfig";

const initializeItemState = (item, config) => ({
  id: item?.id || config.type,
  data: {
    ...item,
    is_locked: item?.is_locked || false, // Ensure is_locked is always present in data
  },
  isLoading: false,
});

// Define the initial state structure
const initialState = KB_CONFIG.reduce((acc, config) => {
  acc[config.type] = {
    items: [],
    isLoading: false,
    fetched: false,
    auth: config.auth,
    scope: config.scope,
    wordThreshold: config.wordThreshold,
    lastTriggerWordCount: config.scope === "book" ? 0 : {},
    dataStructure: config.dataStructure || "list", // 'list' or 'single'
  };
  return acc;
}, {});

// Add word counts to initial state
initialState.wordCounts = {
  chapterWordCounts: {},
  bookWordCount: 0,
};

// Action types
const SET_DATA = "SET_DATA";
const SET_LOADING = "SET_LOADING";
const SET_ITEM_LOADING = "SET_ITEM_LOADING";
const SET_ITEM_LOCKED = "SET_ITEM_LOCKED";
const UPDATE_ITEM = "UPDATE_ITEM";
const UPDATE_LAST_TRIGGER = "UPDATE_LAST_TRIGGER";
const SET_CHAPTER_WORD_COUNT = "SET_CHAPTER_WORD_COUNT";
const RESET_WORD_COUNTS = "RESET_WORD_COUNTS";
const DELETE_ITEM = "DELETE_ITEM";
const ADD_ITEM = "ADD_ITEM";
const SET_FETCHED = "SET_FETCHED";

// Reducer
function knowledgeReducer(state, action) {
  const knowledgeType = state[action.knowledgeType];

  switch (action.type) {
    case SET_DATA:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          fetched: true,
          items:
            knowledgeType.dataStructure === "list"
              ? action.payload.map((item) => initializeItemState(item, knowledgeType))
              : [initializeItemState(action.payload, knowledgeType)],
        },
      };
    case SET_FETCHED:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          fetched: action.payload,
        },
      };
    case SET_LOADING:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          isLoading: action.payload,
        },
      };
    case SET_ITEM_LOADING:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          items: knowledgeType.items.map((item) => (item.id === action.itemId ? { ...item, isLoading: action.isLoading } : item)),
          isLoading: action.isLoading || knowledgeType.items.some((item) => item.id !== action.itemId && item.isLoading),
        },
      };
    case SET_ITEM_LOCKED:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          items: knowledgeType.items.map((item) =>
            item.id === action.itemId ? { ...item, data: { ...item.data, is_locked: action.isLocked } } : item,
          ),
        },
      };
    case UPDATE_ITEM:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          items: knowledgeType.items.map((item) =>
            item.id === action.itemId ? { ...item, data: { ...item.data, ...action.updates, updated_at: new Date().toISOString() } } : item,
          ),
        },
      };
    case UPDATE_LAST_TRIGGER:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          lastTriggerWordCount:
            knowledgeType.scope === "book" ? action.payload : { ...knowledgeType.lastTriggerWordCount, [action.chapterId]: action.payload },
        },
      };
    case SET_CHAPTER_WORD_COUNT:
      const updatedChapterWordCounts = {
        ...state.wordCounts.chapterWordCounts,
        [action.payload.chapterId]: action.payload.wordCount,
      };
      const updatedBookWordCount = Object.values(updatedChapterWordCounts).reduce((total, count) => total + count, 0);
      return {
        ...state,
        wordCounts: {
          chapterWordCounts: updatedChapterWordCounts,
          bookWordCount: updatedBookWordCount,
        },
      };

    case RESET_WORD_COUNTS:
      return {
        ...state,
        wordCounts: {
          ...state.wordCounts,
          chapterWordCounts: {},
          bookWordCount: 0,
        },
      };
    case DELETE_ITEM:
      return {
        ...state,
        [action.knowledgeType]: {
          ...knowledgeType,
          items: knowledgeType.items.filter((item) => item.id !== action.itemId),
        },
      };
    case ADD_ITEM:
      const knowledgeState = state[action.knowledgeType];
      if (knowledgeState.dataStructure === "single") {
        // For single data structure, always replace the existing item
        return {
          ...state,
          [action.knowledgeType]: {
            ...knowledgeState,
            items: [initializeItemState(action.payload, knowledgeState)],
          },
        };
      } else {
        // For list data structure
        const existingItemIndex = knowledgeState.items.findIndex((item) => item.id === action.payload.id);
        if (existingItemIndex !== -1) {
          // Update existing item
          return {
            ...state,
            [action.knowledgeType]: {
              ...knowledgeState,
              items: knowledgeState.items.map((item) =>
                item.id === action.payload.id ? initializeItemState({ ...item.data, ...action.payload }, knowledgeState) : item,
              ),
            },
          };
        } else {
          // Add new item at the start or end based on insertAtStart flag
          return {
            ...state,
            [action.knowledgeType]: {
              ...knowledgeState,
              items: action.insertAtStart
                ? [initializeItemState(action.payload, knowledgeState), ...knowledgeState.items]
                : [...knowledgeState.items, initializeItemState(action.payload, knowledgeState)],
            },
          };
        }
      }

    default:
      return state;
  }
}

// Create context
const KnowledgeContext = createContext();

// Provider component
export function KnowledgeProvider({ children }) {
  const [state, dispatch] = useReducer(knowledgeReducer, initialState);

  return <KnowledgeContext.Provider value={{ state, dispatch }}>{children}</KnowledgeContext.Provider>;
}

// Custom hook to use the knowledge context
export function useKnowledge() {
  const context = useContext(KnowledgeContext);
  if (!context) {
    throw new Error("useKnowledge must be used within a KnowledgeProvider");
  }
  return context;
}

// Action creators
export const setData = (knowledgeType, data) => ({
  type: SET_DATA,
  knowledgeType,
  payload: data,
});

export const setLoading = (knowledgeType, isLoading) => ({
  type: SET_LOADING,
  knowledgeType,
  payload: isLoading,
});

export const setItemLoading = (knowledgeType, itemId, isLoading) => {
  return {
    type: SET_ITEM_LOADING,
    knowledgeType,
    itemId,
    isLoading,
  };
};

export const setItemLocked = (knowledgeType, itemId, isLocked) => ({
  type: SET_ITEM_LOCKED,
  knowledgeType,
  itemId,
  isLocked,
});

export const addItem = (knowledgeType, newItem, insertAtStart = false) => ({
  type: ADD_ITEM,
  knowledgeType,
  payload: newItem,
  insertAtStart,
});

export const deleteItem = (knowledgeType, itemId) => ({
  type: DELETE_ITEM,
  knowledgeType,
  itemId,
});

export const updateItem = (knowledgeType, itemId, updates) => ({
  type: UPDATE_ITEM,
  knowledgeType,
  itemId,
  updates,
});

export const updateLastTrigger = (knowledgeType, wordCount, chapterId = null) => ({
  type: UPDATE_LAST_TRIGGER,
  knowledgeType,
  payload: wordCount,
  chapterId,
});

export const setChapterWordCount = (chapterId, wordCount) => ({
  type: SET_CHAPTER_WORD_COUNT,
  payload: { chapterId, wordCount },
});

export const resetWordCounts = () => ({
  type: RESET_WORD_COUNTS,
});

// Helper functions for easier state access
export const getKnowledgeData = (state, knowledgeType) => {
  const knowledgeState = state[knowledgeType];
  return knowledgeState.dataStructure === "single" ? knowledgeState.items[0]?.data : knowledgeState.items.map((item) => item.data);
};

export const getKnowledgeItem = (state, knowledgeType, itemId) => {
  return state[knowledgeType].items.find((item) => item.id === itemId);
};

export const isKnowledgeLoading = (state, knowledgeType) => state[knowledgeType].isLoading;
export const isKnowledgeFetched = (state, knowledgeType) => state[knowledgeType].fetched;

export const isKnowledgeItemLoading = (state, knowledgeType, itemId) => {
  const item = getKnowledgeItem(state, knowledgeType, itemId);
  return item ? item.isLoading : false;
};

export const isKnowledgeItemLocked = (state, knowledgeType, itemId) => {
  const item = getKnowledgeItem(state, knowledgeType, itemId);
  return item ? item.data.is_locked : false;
};

export const getChapterWordCount = (state, chapterId) => state.wordCounts.chapterWordCounts[chapterId] || 0;
export const getBookWordCount = (state) => state.wordCounts.bookWordCount;
